import {
    type Social,
    SocialButton,
    type SocialButtonSize,
    type SocialButtonTheme,
} from '@trustoo/components/SocialButton/v2'
import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef, MouseEventHandler, ReactNode } from 'react'

type SocialButtonGroupStyle = 'buttons' | 'icons'

interface SocialButtonGroupItem {
    social: Social
    label?: ReactNode
    onClick?: MouseEventHandler<HTMLButtonElement>
    disabled?: boolean
}

interface SocialButtonGroupProps extends Omit<
    ComponentPropsWithoutRef<'div'>,
    'children' | 'style'
> {
    items: SocialButtonGroupItem[]
    style?: SocialButtonGroupStyle
    theme?: SocialButtonTheme
    size?: SocialButtonSize
}

const LAYOUT_CLASSES: Record<SocialButtonGroupStyle, string> = {
    buttons: 'flex flex-col items-stretch',
    icons: 'flex flex-row items-center',
}

const GAP_CLASSES: Record<SocialButtonGroupStyle, Record<SocialButtonSize, string>> = {
    buttons: {
        md: 'gap-2.5',
        lg: 'gap-spacing-lg',
    },
    icons: {
        md: 'gap-spacing-lg',
        lg: 'gap-spacing-lg',
    },
}

const ITEM_CLASSES: Record<SocialButtonGroupStyle, string | undefined> = {
    buttons: 'w-full',
    icons: undefined,
}

const SocialButtonGroup = forwardRef<HTMLDivElement, SocialButtonGroupProps>(
    ({ items, style = 'buttons', theme = 'brand', size = 'md', className, ...props }, ref) => (
        <div
            ref={ref}
            role="group"
            className={clsx(LAYOUT_CLASSES[style], GAP_CLASSES[style][size], className)}
            {...props}>
            {items.map((item, index) => (
                <SocialButton
                    key={`${item.social}-${index}`}
                    social={item.social}
                    theme={theme}
                    size={size}
                    iconOnly={style === 'icons'}
                    label={item.label}
                    onClick={item.onClick}
                    disabled={item.disabled}
                    className={ITEM_CLASSES[style]}
                />
            ))}
        </div>
    ),
)

SocialButtonGroup.displayName = 'SocialButtonGroup'

export {
    SocialButtonGroup,
    type SocialButtonGroupProps,
    type SocialButtonGroupItem,
    type SocialButtonGroupStyle,
}
